import { useEffect, useMemo, useState } from 'react';
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, BarChart3, Check, ChevronDown, ChevronsUpDown, CreditCard, Edit2, ExternalLink, Link2, LogIn, Plus, Search,
    Trash2, Users, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { useTeamsList } from '@/hooks/teams/useTeamsList';
import { useImpersonate } from '@/hooks/users/useImpersonate';
import { useDeleteUser, useProvisionGenieMailboxes } from '@/hooks/users/useUserManagement';
import { useUsersUsageSummary } from '@/hooks/users/useUsersUsageSummary';
import { UsersSortBy, UsersSortOrder, useUsersWithSubscriptions } from '@/hooks/users/useUsersWithSubscriptions';
import { cn } from '@/lib/utils';
import { ImpersonationService } from '@/services/users/ImpersonationService';
import { ProfileWithSubscription } from '@/types/auth/auth';
import { MergeUsersDialog } from './components/MergeUsersDialog';
import { MergeUsersProgressDialog } from './components/MergeUsersProgressDialog';
import { MergeUsersToolbar } from './components/MergeUsersToolbar';
import { StripeCustomerLookupSheet } from './components/StripeCustomerLookupSheet';
import { UnlinkedStripeSubscriptionsSheet } from './components/UnlinkedStripeSubscriptionsSheet';
import { UserCreateDialog } from './components/UserCreateDialog';
import { UserEditDialog } from './components/UserEditDialog';
import { getRoleBadgeColor, getRoleLabel, getSubscriptionStatusBadge } from './utils/userBadges';

const UsersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const editEmail = searchParams.get("edit");
  const redirectParam = searchParams.get("redirect");
  const impersonationRedirect =
    redirectParam?.startsWith("/") && !redirectParam.startsWith("//") ? redirectParam : null;
  const [searchTerm, setSearchTerm] = useState(editEmail || "");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(editEmail || "");
  const [roleFilter, setRoleFilter] = useState<string>("");
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>("");
  const [needsAttentionFilter, setNeedsAttentionFilter] = useState(false);
  const [teamFilter, setTeamFilter] = useState<{ id: number; name: string } | null>(null);
  const [teamFilterOpen, setTeamFilterOpen] = useState(false);
  const [teamFilterSearch, setTeamFilterSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<UsersSortBy | "last_activity">("created_at");
  const [sortOrder, setSortOrder] = useState<UsersSortOrder>("desc");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isUnlinkedStripeOpen, setIsUnlinkedStripeOpen] = useState(false);
  const [isStripeLookupOpen, setIsStripeLookupOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<ProfileWithSubscription | null>(null);
  const [editingUserTab, setEditingUserTab] = useState<'profile' | 'subscription' | 'links'>('profile');
  const [userToDelete, setUserToDelete] = useState<ProfileWithSubscription | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedUsersById, setSelectedUsersById] = useState<Map<string, ProfileWithSubscription>>(new Map());
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);
  const [mergeJobId, setMergeJobId] = useState<number | null>(null);
  const [isMergeProgressOpen, setIsMergeProgressOpen] = useState(false);

  const navigate = useNavigate();
  const { impersonate } = useImpersonate();
  const deleteUserMutation = useDeleteUser();
  const provisionGenieMailboxesMutation = useProvisionGenieMailboxes({ showToast: false });
  const [isBulkGeneratingMailboxes, setIsBulkGeneratingMailboxes] = useState(false);
  const currentImpersonatedUserId = ImpersonationService.getImpersonatedUserId();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data: teamOptionsData, isLoading: isSearchingTeams } = useTeamsList(teamFilterSearch, 1, 20);
  const sortedTeamOptions = useMemo(
    () => [...(teamOptionsData?.teams ?? [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [teamOptionsData],
  );

  const { data: usersData, isLoading } = useUsersWithSubscriptions(
    {
      search: debouncedSearchTerm,
      role: roleFilter || undefined,
      hasSubscription: subscriptionFilter ? subscriptionFilter === "active" : undefined,
      teamId: teamFilter?.id,
      needsAttention: needsAttentionFilter || undefined,
    },
    page,
    50,
    sortBy === "last_activity" ? "created_at" : sortBy,
    sortOrder,
  );

  const fetchedUsers = usersData?.profiles ?? [];
  const totalCount = usersData?.count ?? 0;
  const attentionCount = usersData?.attentionCount ?? 0;
  const pageSize = 50;
  const totalPages = Math.ceil(totalCount / pageSize);

  const ownerIds = useMemo(
    () => fetchedUsers.filter((u) => u.role !== "consumer").map((u) => u.id),
    [fetchedUsers],
  );

  // Auto-open the edit dialog when arriving with ?edit=<email>
  useEffect(() => {
    if (!editEmail || isLoading) return;

    const matchedUser = fetchedUsers.find((u) => u.email?.toLowerCase() === editEmail.toLowerCase());
    if (matchedUser) {
      setEditingUser(matchedUser);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("edit");
        return next;
      }, { replace: true });
    }
  }, [editEmail, isLoading, fetchedUsers, setSearchParams]);
  const { data: usageMap, isLoading: isUsageLoading } = useUsersUsageSummary(ownerIds);

  const users = useMemo(() => {
    if (sortBy !== "last_activity") return fetchedUsers;
    const withActivity = fetchedUsers.map((u) => ({
      user: u,
      activity: usageMap?.[u.id]?.lastActivityAt ? new Date(usageMap[u.id].lastActivityAt as string).getTime() : -Infinity,
    }));
    withActivity.sort((a, b) => (sortOrder === "asc" ? a.activity - b.activity : b.activity - a.activity));
    return withActivity.map((entry) => entry.user);
  }, [fetchedUsers, sortBy, sortOrder, usageMap]);

  const isConsumer = (user: ProfileWithSubscription) => user.role === "consumer";

  const formatShortRelative = (iso: string): string => {
    const date = new Date(iso);
    if (isNaN(date.getTime())) return "Never";
    const diff = Date.now() - date.getTime();
    const minutes = Math.floor(diff / 60_000);
    const hours = Math.floor(diff / 3_600_000);
    const days = Math.floor(diff / 86_400_000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  };

  const formatLastActivity = (iso: string | null): { label: string; tooltip: string | null } => {
    if (!iso) return { label: "Never", tooltip: null };
    try {
      const date = new Date(iso);
      if (isNaN(date.getTime())) return { label: "Never", tooltip: null };
      return {
        label: formatShortRelative(iso),
        tooltip: date.toLocaleString(),
      };
    } catch {
      return { label: "Never", tooltip: null };
    }
  };

  const handleSort = (column: UsersSortBy | "last_activity") => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder(column === "full_name" || column === "email" ? "asc" : "desc");
    }
    setPage(1);
  };

  const renderSortIcon = (column: UsersSortBy | "last_activity") => {
    if (sortBy !== column) return <ArrowUpDown className="h-3 w-3 text-gray-400" />;
    return sortOrder === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, roleFilter, subscriptionFilter, teamFilter, needsAttentionFilter]);

  const handleDeleteUser = (user: ProfileWithSubscription) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (userToDelete) {
      deleteUserMutation.mutate(userToDelete.id);
    }
  };

  const toggleUserSelected = (user: ProfileWithSubscription, checked: boolean) => {
    setSelectedUsersById((prev) => {
      const next = new Map(prev);
      if (checked) next.set(user.id, user);
      else next.delete(user.id);
      return next;
    });
  };

  const selectedUsers = useMemo(() => Array.from(selectedUsersById.values()), [selectedUsersById]);

  const handleMergeStarted = (jobId: number) => {
    setMergeJobId(jobId);
    setIsMergeProgressOpen(true);
    setSelectedUsersById(new Map());
  };

  const handleBulkGenerateMailboxes = async () => {
    if (selectedUsers.length === 0) return;

    setIsBulkGeneratingMailboxes(true);
    try {
      const results = await Promise.allSettled(
        selectedUsers.map((user) => provisionGenieMailboxesMutation.mutateAsync(user.id)),
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      const started = results.length - failed;

      if (started > 0) {
        toast.success(`Started mailbox generation for ${started} user${started === 1 ? "" : "s"}`);
      }
      if (failed > 0) {
        toast.error(`Failed to start mailbox generation for ${failed} user${failed === 1 ? "" : "s"}`);
      }
      setSelectedUsersById(new Map());
    } finally {
      setIsBulkGeneratingMailboxes(false);
    }
  };

  const openStripeCustomer = (stripeId: string | undefined) => {
    if (!stripeId) {
      toast.error("No Stripe customer ID found");
      return;
    }
    window.open(`https://dashboard.stripe.com/customers/${stripeId}`, "_blank");
  };

  const openStripeSubscription = (subscriptionStripeId: string | null | undefined) => {
    if (!subscriptionStripeId) {
      toast.error("No Stripe subscription ID found");
      return;
    }
    window.open(`https://dashboard.stripe.com/subscriptions/${subscriptionStripeId}`, "_blank");
  };

  useEffect(() => {
    if (deleteUserMutation.isSuccess) {
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  }, [deleteUserMutation.isSuccess]);

  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, totalCount);

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto px-4 py-8 max-w-[1920px]">
        <div className="w-full">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 md:mb-8">
            <div className="flex items-center">
              <button className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3 md:mr-4 hover:bg-genie-primary/20 transition-colors">
                <Users className="h-4 w-4 md:h-5 md:w-5 text-genie-primary" />
              </button>
              <h1 className="text-xl md:text-3xl font-bold text-[#222B45]">User Management</h1>
            </div>
            {/* WorkspaceSwitcher is fixed at `right-6 top-5` (md and up), so the header actions stop short of it. */}
            <div className="flex gap-2 w-full sm:w-auto md:pr-[104px]">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex-1 sm:flex-none min-h-[44px] sm:min-h-0 gap-2" size="sm">
                    <CreditCard className="h-4 w-4" />
                    Stripe
                    <ChevronDown className="h-4 w-4 opacity-60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => setIsStripeLookupOpen(true)}>
                    <Search className="h-4 w-4 mr-2" />
                    Customer lookup
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setIsUnlinkedStripeOpen(true)}>
                    <Link2 className="h-4 w-4 mr-2" />
                    Unlinked subscriptions
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                className="bg-genie-primary hover:bg-genie-primary/90 text-white flex-1 sm:flex-none min-h-[44px] sm:min-h-0"
                onClick={() => setIsCreateDialogOpen(true)}
                size="sm"
              >
                <Plus className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Create User</span>
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-4 md:p-6">
              {/* Filters */}
              <div className="mb-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_150px_170px_200px_auto] gap-4 lg:gap-3">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search by name, email, or ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 text-sm"
                    />
                  </div>

                  {/* Role Filter */}
                  <Select value={roleFilter || "all"} onValueChange={(val) => setRoleFilter(val === "all" ? "" : val)}>
                    <SelectTrigger className="text-sm">
                      <SelectValue placeholder="Filter by role..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      <SelectItem value="internal_admin">Admin</SelectItem>
                      <SelectItem value="standard_user">Business</SelectItem>
                      <SelectItem value="consumer">Consumer</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Subscription Filter */}
                  <Select value={subscriptionFilter || "all"} onValueChange={(val) => setSubscriptionFilter(val === "all" ? "" : val)}>
                    <SelectTrigger className="text-sm">
                      <SelectValue placeholder="Filter by subscription..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Users</SelectItem>
                      <SelectItem value="active">Has Subscription</SelectItem>
                      <SelectItem value="inactive">No Subscription</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Team Filter */}
                  <Popover
                    open={teamFilterOpen}
                    onOpenChange={(open) => {
                      setTeamFilterOpen(open);
                      if (!open) setTeamFilterSearch("");
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={teamFilterOpen}
                        className="w-full justify-between font-normal text-sm"
                      >
                        <span className="truncate">{teamFilter ? teamFilter.name : "Filter by team..."}</span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                      <Command filter={() => 1}>
                        <CommandInput
                          placeholder="Search teams..."
                          value={teamFilterSearch}
                          onValueChange={setTeamFilterSearch}
                        />
                        <CommandList>
                          <CommandEmpty>{isSearchingTeams ? "Searching..." : "No team found."}</CommandEmpty>
                          <CommandGroup>
                            <CommandItem
                              value="all"
                              onSelect={() => {
                                setTeamFilter(null);
                                setTeamFilterOpen(false);
                              }}
                            >
                              <Check className={cn("mr-2 h-4 w-4", !teamFilter ? "opacity-100" : "opacity-0")} />
                              All Teams
                            </CommandItem>
                            {sortedTeamOptions.map((team) => (
                              <CommandItem
                                key={team.id}
                                value={String(team.id)}
                                onSelect={() => {
                                  setTeamFilter({ id: team.id, name: team.name || "Unnamed Team" });
                                  setTeamFilterOpen(false);
                                }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", teamFilter?.id === team.id ? "opacity-100" : "opacity-0")} />
                                {team.name || "Unnamed Team"}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>

                  <div className="flex flex-wrap items-center gap-2 empty:hidden sm:col-span-2 lg:col-span-1">
                    {(attentionCount > 0 || needsAttentionFilter) && (
                      <button
                        type="button"
                        onClick={() => setNeedsAttentionFilter((value) => !value)}
                        aria-pressed={needsAttentionFilter}
                        className={cn(
                          "inline-flex items-center gap-1.5 min-h-[44px] sm:min-h-0 sm:h-10 px-3 rounded-full text-sm font-medium border whitespace-nowrap transition-colors",
                          needsAttentionFilter
                            ? "bg-genie-accent/15 text-genie-accent-dark border-genie-accent/40"
                            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50",
                        )}
                      >
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Needs attention
                        <span
                          className={cn(
                            "ml-0.5 rounded-full px-1.5 text-xs leading-5",
                            needsAttentionFilter ? "bg-genie-accent/25" : "bg-gray-100 text-gray-600",
                          )}
                        >
                          {attentionCount}
                        </span>
                      </button>
                    )}

                    {(searchTerm || roleFilter || subscriptionFilter || teamFilter || needsAttentionFilter) && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          setSearchTerm("");
                          setRoleFilter("");
                          setSubscriptionFilter("");
                          setTeamFilter(null);
                          setNeedsAttentionFilter(false);
                        }}
                        className="ml-auto lg:ml-0 min-h-[44px] sm:min-h-0 sm:h-10 px-3 text-gray-500 hover:text-gray-700 text-sm whitespace-nowrap"
                      >
                        Clear all
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <MergeUsersToolbar
                selectedCount={selectedUsersById.size}
                onMerge={() => setIsMergeDialogOpen(true)}
                onClear={() => setSelectedUsersById(new Map())}
                onGenerateMailboxes={handleBulkGenerateMailboxes}
                isGeneratingMailboxes={isBulkGeneratingMailboxes}
              />

              {/* Loading State */}
              {isLoading ? (
                <div className="text-center py-8 text-gray-500">Loading users...</div>
              ) : users.length === 0 ? (
                <div className="text-center text-gray-500 py-8 text-sm">
                  {searchTerm || roleFilter ? "No users match your filters" : "No users found"}
                </div>
              ) : (
                <>
                  {/* Table */}
                  <div className="rounded-md border overflow-x-auto w-full">
                    <Table className="w-full">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10">
                            <span className="sr-only">Select</span>
                          </TableHead>
                          <TableHead>
                            <button
                              type="button"
                              onClick={() => handleSort("full_name")}
                              className="flex items-center gap-1 hover:text-gray-900"
                            >
                              User
                              {renderSortIcon("full_name")}
                            </button>
                          </TableHead>
                          <TableHead className="hidden md:table-cell">
                            <button
                              type="button"
                              onClick={() => handleSort("email")}
                              className="flex items-center gap-1 hover:text-gray-900"
                            >
                              Email
                              {renderSortIcon("email")}
                            </button>
                          </TableHead>
                          <TableHead className="hidden lg:table-cell">Subscription / Role</TableHead>
                          <TableHead className="hidden lg:table-cell">Team</TableHead>
                          <TableHead className="hidden xl:table-cell">
                            <button
                              type="button"
                              onClick={() => handleSort("created_at")}
                              className="flex items-center gap-1 hover:text-gray-900"
                            >
                              Created
                              {renderSortIcon("created_at")}
                            </button>
                          </TableHead>
                          <TableHead className="hidden lg:table-cell text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Genies</TableHead>
                          <TableHead className="hidden lg:table-cell text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">7d Conv.</TableHead>
                          <TableHead className="hidden xl:table-cell text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            <button
                              type="button"
                              onClick={() => handleSort("last_activity")}
                              className="flex items-center gap-1 hover:text-gray-900"
                            >
                              Last Activity
                              {renderSortIcon("last_activity")}
                            </button>
                          </TableHead>
                          <TableHead className="text-right sticky right-0 bg-white">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {users.map((user) => {
                          const isTeamOwner = !!user.team && user.team.owner_id === user.id;
                          return (
                          <TableRow key={user.id}>
                            <TableCell
                              className={`py-2${
                                isTeamOwner
                                  ? " bg-gradient-to-b from-genie-primary to-genie-secondary bg-[length:6px_100%] bg-no-repeat bg-left"
                                  : ""
                              }`}
                            >
                              <Checkbox
                                checked={selectedUsersById.has(user.id)}
                                onCheckedChange={(checked) => toggleUserSelected(user, checked === true)}
                                aria-label={`Select ${user.full_name || user.email || user.id}`}
                              />
                            </TableCell>
                            <TableCell
                              className="py-2 cursor-pointer group"
                              onClick={() => {
                                // Selecting the user id to copy it shouldn't open the drawer.
                                if (window.getSelection()?.toString()) return;
                                setEditingUserTab('profile');
                                setEditingUser(user);
                              }}
                            >
                              <div className="flex items-center gap-3">
                                <div className="h-9 w-9 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                  {user.avatar_url ? (
                                    <img
                                      src={user.avatar_url}
                                      alt={user.full_name || "User"}
                                      className="h-9 w-9 rounded-full object-cover"
                                    />
                                  ) : (
                                    <Users className="h-4 w-4 text-gray-500" />
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <div className="font-medium truncate text-sm group-hover:text-genie-primary transition-colors">
                                    {user.full_name || <span className="text-gray-400 italic">No name</span>}
                                  </div>
                                  <div className="text-xs text-gray-500 truncate">{user.id}</div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="hidden md:table-cell py-2">
                              <div className="flex items-center gap-2">
                                <CopyableEmail email={user.email} fallback="No email" className="text-sm text-gray-700" textClassName="truncate" />
                              </div>
                            </TableCell>
                            <TableCell className="hidden lg:table-cell py-2">
                              <div className="flex items-center gap-2">
                                {getSubscriptionStatusBadge(user.subscription?.status)}
                                {(user.unbilled_phone_numbers?.length ?? 0) > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingUserTab('subscription');
                                      setEditingUser(user);
                                    }}
                                    className="inline-flex items-center gap-1 rounded-full bg-genie-accent/15 px-2 py-0.5 text-xs font-medium text-genie-accent-dark hover:bg-genie-accent/25"
                                    title={`${user.unbilled_phone_numbers!.length} phone number(s) not billed`}
                                  >
                                    <AlertTriangle className="h-3 w-3" />
                                    Attention
                                  </button>
                                )}
                                {user.subscription?.stripe_id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openStripeSubscription(user.subscription?.stripe_id)}
                                    className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700"
                                    title="View in Stripe"
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                              <Badge className={`${getRoleBadgeColor(user.role)} mt-1`}>{getRoleLabel(user.role)}</Badge>
                            </TableCell>
                            <TableCell className="hidden lg:table-cell py-2">
                              {user.team ? (
                                <div>
                                  <div className="text-sm text-gray-700 truncate max-w-[140px]" title={user.team.name ?? undefined}>
                                    {user.team.name}
                                  </div>
                                  <Badge
                                    variant="outline"
                                    className={`mt-1 ${
                                      user.team.owner_id === user.id
                                        ? "text-genie-primary border-genie-primary/30 bg-genie-primary/5"
                                        : "text-gray-500 border-gray-300"
                                    }`}
                                  >
                                    {user.team.owner_id === user.id ? "Owner" : "Member"}
                                  </Badge>
                                </div>
                              ) : (
                                <span className="text-gray-300 text-sm">—</span>
                              )}
                            </TableCell>
                            <TableCell className="hidden xl:table-cell text-sm text-gray-600 py-2">
                              {user.created_at ? new Date(user.created_at).toLocaleDateString() : "Unknown"}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell text-right py-2">
                              {isConsumer(user) ? (
                                <span className="text-gray-300">—</span>
                              ) : isUsageLoading ? (
                                <span className="text-gray-300 text-sm">…</span>
                              ) : (() => {
                                const count = usageMap?.[user.id]?.genieCount ?? 0;
                                return count > 0 ? (
                                  <span className="inline-flex items-center justify-center min-w-[2.25rem] px-2 py-0.5 rounded-md bg-genie-primary/10 text-genie-primary font-semibold text-sm">
                                    {count}
                                  </span>
                                ) : (
                                  <span className="text-gray-300 text-sm">0</span>
                                );
                              })()}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell text-right py-2">
                              {isConsumer(user) ? (
                                <span className="text-gray-300">—</span>
                              ) : isUsageLoading ? (
                                <span className="text-gray-300 text-sm">…</span>
                              ) : (() => {
                                const count = usageMap?.[user.id]?.recentConversations7d ?? 0;
                                return count > 0 ? (
                                  <span className="inline-flex items-center justify-center min-w-[2.25rem] px-2 py-0.5 rounded-md bg-genie-secondary/10 text-genie-secondary font-semibold text-sm">
                                    {count}
                                  </span>
                                ) : (
                                  <span className="text-gray-300 text-sm">0</span>
                                );
                              })()}
                            </TableCell>
                            <TableCell className="hidden xl:table-cell whitespace-nowrap py-2">
                              {isConsumer(user) ? (
                                <span className="text-gray-300">—</span>
                              ) : isUsageLoading ? (
                                <span className="text-gray-300 text-sm">…</span>
                              ) : (() => {
                                const iso = usageMap?.[user.id]?.lastActivityAt ?? null;
                                const { label, tooltip } = formatLastActivity(iso);
                                if (!iso) {
                                  return <span className="text-gray-300 text-sm italic">Never</span>;
                                }
                                const isRecent =
                                  Date.now() - new Date(iso).getTime() < 7 * 24 * 60 * 60 * 1000;
                                const content = (
                                  <span className="inline-flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
                                    <span
                                      className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${isRecent ? "bg-genie-primary" : "bg-gray-300"}`}
                                    />
                                    {label}
                                  </span>
                                );
                                if (!tooltip) return content;
                                return (
                                  <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="cursor-help">{content}</span>
                                      </TooltipTrigger>
                                      <TooltipContent>{tooltip}</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                );
                              })()}
                            </TableCell>
                            <TableCell className="text-right sticky right-0 bg-white py-2">
                              <div className="flex items-center justify-end gap-1">
                                {!isConsumer(user) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate(`/admin/users/${user.id}/analytics`)}
                                    className="bg-gradient-to-r from-genie-primary/10 to-genie-secondary/10 text-genie-primary hover:from-genie-primary/20 hover:to-genie-secondary/20 hover:text-genie-primary"
                                    title="View usage analytics"
                                  >
                                    <BarChart3 className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => impersonate(user.id, user.full_name || user.email || user.id)}
                                  className="text-genie-secondary hover:text-genie-secondary hover:bg-genie-secondary/10"
                                  title={currentImpersonatedUserId === user.id ? "Currently impersonating" : "Impersonate user"}
                                  disabled={currentImpersonatedUserId === user.id}
                                >
                                  <LogIn className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setEditingUserTab('profile');
                                    setEditingUser(user);
                                  }}
                                  className="text-gray-600 hover:text-gray-900"
                                  title="Edit user"
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setEditingUserTab('links');
                                    setEditingUser(user);
                                  }}
                                  className="text-genie-primary hover:text-genie-primary hover:bg-genie-primary/10"
                                  title="Links and Actions"
                                >
                                  <Zap className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteUser(user)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title="Delete user"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalCount > pageSize && (
                    <div className="flex items-center justify-between p-4 border-t border-gray-200 mt-4">
                      <div className="text-xs md:text-sm text-gray-600">
                        Showing {startIndex} to {endIndex} of {totalCount} users
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 1 || isLoading}>
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage(page + 1)}
                          disabled={endIndex >= totalCount || isLoading}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <UnlinkedStripeSubscriptionsSheet isOpen={isUnlinkedStripeOpen} onClose={() => setIsUnlinkedStripeOpen(false)} />
      <StripeCustomerLookupSheet
        isOpen={isStripeLookupOpen}
        onClose={() => setIsStripeLookupOpen(false)}
        onOpenAccount={(email) => {
          setIsStripeLookupOpen(false);
          setSearchTerm(email);
          setDebouncedSearchTerm(email);
          setPage(1);
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("edit", email);
            return next;
          }, { replace: true });
        }}
      />
      <UserCreateDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={() => setIsCreateDialogOpen(false)}
      />

      {editingUser && (
        <UserEditDialog
          key={editingUser.id}
          user={editingUser}
          isOpen={!!editingUser}
          onClose={() => setEditingUser(null)}
          onUpdate={() => setEditingUser(null)}
          onDelete={(user) => {
            handleDeleteUser(user);
            setEditingUser(null);
          }}
          initialTab={editingUserTab}
          impersonationRedirect={impersonationRedirect}
        />
      )}

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg">Delete User</AlertDialogTitle>
            <AlertDialogDescription className="text-sm mt-2">
              Are you sure you want to delete{" "}
              <CopyableEmail email={userToDelete?.full_name || userToDelete?.email} fallback="this user" className="font-medium text-gray-900" />? This
              action cannot be undone and will permanently remove the user's account and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="mt-0">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteUserMutation.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteUserMutation.isPending ? "Deleting..." : "Delete User"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MergeUsersDialog
        users={selectedUsers}
        isOpen={isMergeDialogOpen}
        onClose={() => setIsMergeDialogOpen(false)}
        onMergeStarted={handleMergeStarted}
      />

      <MergeUsersProgressDialog
        isOpen={isMergeProgressOpen}
        jobId={mergeJobId}
        onClose={() => setIsMergeProgressOpen(false)}
      />
    </div>
  );
};

export default UsersPage;
